import React from "react";
import ReactPlayer from "react-player/vimeo";
import { useRecoilValue } from "recoil";
import aneuploidyZygosity from "../../../assets/images/aneuploidyZygosity.png";
import fetalAntigenDetected from "../../../assets/images/fetalAntigenNiptDetected.png";
import fetalAntigenNotDetected from "../../../assets/images/fetalAntigenNiptNotDetected.png";
import lowRisk22qVideo from "../../../assets/images/lowRisk22qVideo.png";
import lowRiskAneuploidyVideo from "../../../assets/images/lowRiskAneuploidyVideo.png";
import rhdDetectedVideo from "../../../assets/images/rhdDetectedVideo.png";
import rhdNotDetectedVideo from "../../../assets/images/rhdNotDetectedVideo.png";
import aneuploidyVideo from "../../../assets/images/unityAneuploidyInfoVideo.png";
import carrierFetalRisk from "../../../assets/images/carrierFetalRisk.png";
import { useDeviceHook } from "../../../utils/hooks/useDeviceHook";
import { getUserAuth } from "../../../recoil/selectors";
import BrowseVideoButton from "../../../pages/Video/BrowseVideoButton";
import { TrackAnalyticsEvent } from "../../../services/Analytics";

export const getWidth = (deviceType: string) => {
  if (deviceType === "mobile") return "328px";
  if (deviceType === "tablet") return "528px";
  if (deviceType === "desktop") return "760px";
};

export const getHeight = (deviceType: string) => {
  if (deviceType === "mobile") return "328px";
  if (deviceType === "tablet") return "296px";
  if (deviceType === "desktop") return "442px";
};

// Maps analytics_prefix to thumbnail preview image.
// Fallback shows generic aneuploidy thumbnail - does not affect which video plays (URL from backend).
// NOTE: Backend will send thumbnail_url directly - this mapping will be removed.
type ReportVideoType = "aneuploidy" | "carrier";

export const getVideoImage = (
  analyticsPrefix: string,
  reportType: ReportVideoType,
): string => {
  if (reportType === "carrier") return carrierFetalRisk;

  const mapping: Record<string, string> = {
    AneuploidyLowRiskVideo: lowRiskAneuploidyVideo,
    AneuploidyNoCallVideo: aneuploidyVideo,
    AneuploidyZygosityVideo: aneuploidyZygosity,
    AneuploidyRhDDetectedVideo: rhdDetectedVideo,
    AneuploidyRhDNotDetectedVideo: rhdNotDetectedVideo,
    AneuploidyDel22qLowRiskVideo: lowRisk22qVideo,
    AneuploidyFetalAntigenDetectedVideo: fetalAntigenDetected,
    AneuploidyFetalAntigenNotDetectedVideo: fetalAntigenNotDetected,
  };
  return mapping[analyticsPrefix] || aneuploidyVideo;
};

const ReportVideoEmbed: React.FC<{
  requisitionID: string;
  url: string;
  analyticsPrefix: string;
  reportType: ReportVideoType;
}> = ({ requisitionID, url, analyticsPrefix, reportType }) => {
  const { deviceType } = useDeviceHook();
  const auth = useRecoilValue(getUserAuth);
  const thumbnail = getVideoImage(analyticsPrefix, reportType);

  return (
    <>
      <ReactPlayer
        playing
        url={url}
        light={thumbnail}
        height={getHeight(deviceType)}
        width={getWidth(deviceType)}
        controls
        data-testid="unity-video-player"
        data-vimeo-playsinline
        data-vimeo-autoplay
        onPlay={async () => {
          await TrackAnalyticsEvent(
            `${analyticsPrefix}Clicked`,
            auth?.access_token,
            auth?.user_id,
            { requisitionId: requisitionID },
          );
        }}
      />
      <div
        style={{
          textAlign: "center",
          paddingTop: "10px",
          marginBottom: "-18px",
        }}
      >
        <BrowseVideoButton />
      </div>
    </>
  );
};

export default ReportVideoEmbed;
